// Hook para gerenciar locks e inicialização da edição
import { useState, useCallback } from 'react';
import { useLock } from '@/hooks/useLock';
import { useI18n } from '@/contexts/i18n-hook';

interface UseApreciacaoLockProps {
  apreciacaoId: string | undefined;
  usuarioId: string;
  onLockBlocked?: () => void;
}

export function useApreciacaoLock({ 
  apreciacaoId, 
  usuarioId,
  onLockBlocked 
}: UseApreciacaoLockProps) {
  const { t, formatarDataHora } = useI18n();
  const [lockInicializado, setLockInicializado] = useState(false);
  const [erroLock, setErroLock] = useState<string | null>(null);

  /**
   * Texto do bloqueio: frases inteiras, uma por linha. O nome de quem travou e
   * a data de expiração só entram quando o backend os devolve.
   */
  const montarMensagemBloqueio = useCallback(
    (usuarioNome?: string | null, expiresAt?: string | Date | null) => {
      const linhas = [t('apreciacoes.salvar.lock.editadaPorOutro'), ''];
      if (usuarioNome) linhas.push(t('apreciacoes.salvar.lock.bloqueadoPor', { usuario: usuarioNome }));
      if (expiresAt) linhas.push(t('apreciacoes.salvar.lock.expiraEm', { data: formatarDataHora(expiresAt) }));
      return linhas.join('\n');
    },
    [t, formatarDataHora]
  );

  const {
    lockInfo,
    loading: lockLoading,
    error: lockError,
    criar: criarLock,
    remover: removerLock,
    renovar,
    verificar: verificarLock,
    eMeuLock,
    bloqueadoPorOutro,
  } = useLock('apreciacao', apreciacaoId, {
    autoLock: false,
    autoRenovar: true,
    enableHeartbeat: true,
    intervaloRenovacao: 5 * 60 * 1000,
    onBloqueado: (info) => {
      setErroLock(montarMensagemBloqueio(info.lock?.usuarioNome, info.lock?.expiresAt));
      setLockInicializado(true);
      onLockBlocked?.();
    },
  });

  const inicializarLock = useCallback(async (): Promise<boolean> => {
    if (!apreciacaoId) return false;

    setLockInicializado(false);
    setErroLock(null);

    try {
      // Verificar se está bloqueado
      const lockVerificado = await verificarLock();
      
      if (lockVerificado?.bloqueado && lockVerificado.lock) {
        const meuLock = lockVerificado.lock.usuarioId === usuarioId;
        
        if (!meuLock) {
          setErroLock(
            montarMensagemBloqueio(lockVerificado.lock.usuarioNome, lockVerificado.lock.expiresAt)
          );
          setLockInicializado(true);
          return false;
        }
      } else {
        // Tentar criar lock
        const lockCriado = await criarLock();
        
        if (!lockCriado) {
          const lockVerificadoNovamente = await verificarLock();
          if (lockVerificadoNovamente?.bloqueado && lockVerificadoNovamente.lock) {
            const meuLock = lockVerificadoNovamente.lock.usuarioId === usuarioId;
            
            if (!meuLock) {
              setErroLock(
                montarMensagemBloqueio(
                  lockVerificadoNovamente.lock.usuarioNome,
                  lockVerificadoNovamente.lock.expiresAt
                )
              );
              setLockInicializado(true);
              return false;
            }
          }
        }
      }

      setLockInicializado(true);
      return true;
    } catch (error) {
      console.error('Erro ao inicializar lock:', error);
      setErroLock(t('apreciacoes.salvar.lock.erroVerificar'));
      setLockInicializado(true);
      return false;
    }
  }, [apreciacaoId, usuarioId, verificarLock, criarLock, montarMensagemBloqueio, t]);

  return {
    lockInfo,
    lockLoading,
    lockError,
    lockInicializado,
    erroLock,
    removerLock,
    renovar,
    eMeuLock,
    bloqueadoPorOutro,
    inicializarLock,
  };
}

